import { Agent, Contributor, Work } from "@biblioteksentralen/cordata";
import { Box, Heading, VStack } from "@biblioteksentralen/react";
import { sort } from "radash";
import { DefaultContainer } from "../../../components/ContentContainer";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { formatAgentName } from "../../cordata/agents";
import { ContributorRoleCode, getContributorRoleLabel, getContributorRoleSortIndex } from "../../cordata/roles";
import { ExternalAgentData } from "../../externalDataFetchers/fetchExternalAgentData";
import { CordataDevInfo } from "../CordataDevInfo";
import { FacetLinks } from "../SubjectLinks";
import { AgentViewBanner } from "./AgentViewBanner";
import { AgentWorksSection } from "./AgentWorksSection";
import { CordataSearchFacetTerm } from "@libry-content/integrations";
import { AgentWorksData } from "../../../app/[locale]/sites/[siteDomain]/samling/personer/[agentBibbiIdAndNorafId]/fetchAgentWorksData";
import { getLanguagesSignature, LanguagesSignature } from "@libry-content/integrations";

const sortWorksByRole = <T,>(worksByContributorRoleLabel: Partial<Record<ContributorRoleCode, T>>) =>
  sort(Object.entries(worksByContributorRoleLabel) as [ContributorRoleCode, T][], ([roleCode]) =>
    getContributorRoleSortIndex(roleCode)
  );

const isAmongContributors = (contributors: Contributor[], agentId: string, roleCode: ContributorRoleCode) =>
  contributors.some(
    ({ agent, roles }) => agent.id === agentId && roles.some(({ marc21Code }) => marc21Code === roleCode)
  );

const contributorRepresentativeLanguages = (
  { contributors, expressions = [], languages }: Work,
  agentId: string,
  roleCode: ContributorRoleCode
): LanguagesSignature | undefined => {
  const isWorkContributor = isAmongContributors(contributors, agentId, roleCode);

  // This choice of language means that when the the agent is a contributor directly on the work,
  // it's displayed in the original language. If we want something else this can be changed
  // (e.g. choosing a prioritized language from the expressions)
  if (isWorkContributor) return getLanguagesSignature(languages);

  const contributorExpression = expressions.find(
    (expression) =>
      isAmongContributors(expression.contributors, agentId, roleCode) ||
      [...(expression.manifestations ?? []), ...(expression.aggregateManifestations ?? [])].some((manifestation) =>
        isAmongContributors(manifestation.contributors, agentId, roleCode)
      )
  );

  return contributorExpression ? getLanguagesSignature(contributorExpression.languages) : undefined;
};

type AgentViewProps = {
  agent: Agent;
  externalData: ExternalAgentData | null;
  initialAgentWorksData: AgentWorksData;
  subjectsFromfacet: CordataSearchFacetTerm[] | null;
};

export const AgentView = ({ agent, externalData, initialAgentWorksData, subjectsFromfacet }: AgentViewProps) => {
  const { t, ts, lang } = useTranslation();

  const name = formatAgentName(ts, agent);

  const worksDataForContributorRoles = initialAgentWorksData.worksDataForContributorRoles;

  // Works for which the agent is both contributor and subject (e.g. autobiographies) are only shown
  // in the contributor section. Among other reasons this is to avoid the two showing representative
  // manifestations with different languages etc.

  const sortedWorksDataForContributorRoles =
    worksDataForContributorRoles && sortWorksByRole(worksDataForContributorRoles);

  return (
    <DefaultContainer position="relative" margin="0 auto" padding="0 1rem">
      <CordataDevInfo
        data={{ agent, worksDataForContributorRoles, worksAboutAgent: initialAgentWorksData.worksDataAboutAgent.works }}
        zIndex="5"
        title="person"
        position="absolute"
        top="-2rem"
        right="0.5rem"
      />
      <AgentViewBanner agent={agent} externalData={externalData}></AgentViewBanner>
      <VStack alignItems="flex-start" spacing="4rem" marginTop="3rem">
        {sortedWorksDataForContributorRoles?.map(([roleCode, worksDataForSection]) => (
          <AgentWorksSection
            key={roleCode}
            agentWorksSectionUrlParam={roleCode}
            heading={`${t("Som")} ${getContributorRoleLabel(lang, roleCode)}`}
            getRepresentativeLanguages={(work) => contributorRepresentativeLanguages(work, agent.id, roleCode)}
            agent={agent}
            initialAgentWorksData={worksDataForSection}
          />
        ))}
        {!!subjectsFromfacet?.length && (
          <Box as="section">
            <Heading as="h2" fontSize="3xl" marginBottom="1rem">
              {t("Emner knyttet til {personNavn}", { personNavn: name })}
            </Heading>
            <FacetLinks facetTerms={subjectsFromfacet} />
          </Box>
        )}
        {!!initialAgentWorksData.worksDataAboutAgent?.works.length && (
          <AgentWorksSection
            agentWorksSectionUrlParam={"aboutAgent"}
            heading={t("Om {personNavn}", { personNavn: name })}
            agent={agent}
            initialAgentWorksData={initialAgentWorksData.worksDataAboutAgent}
          />
        )}
        {/* TODO: Inkludere anbefalinger på lignende forfattere? */}
      </VStack>
    </DefaultContainer>
  );
};
